import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { 
  getFirestore, 
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
  enableNetwork,
  disableNetwork,
  initializeFirestore,
  CACHE_SIZE_UNLIMITED
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAnalytics, isSupported } from 'firebase/analytics';

// Firebase configuration - trim any whitespace/newlines from environment variables
const cleanEnvVar = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  // Remove any newlines, carriage returns, and trim whitespace
  return value.replace(/[\r\n]/g, '').trim();
};

const firebaseConfig = {
  apiKey: cleanEnvVar(process.env.REACT_APP_FIREBASE_API_KEY),
  authDomain: cleanEnvVar(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN),
  projectId: cleanEnvVar(process.env.REACT_APP_FIREBASE_PROJECT_ID),
  storageBucket: cleanEnvVar(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: cleanEnvVar(process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID),
  appId: cleanEnvVar(process.env.REACT_APP_FIREBASE_APP_ID),
  measurementId: cleanEnvVar(process.env.REACT_APP_FIREBASE_MEASUREMENT_ID)
};

// Log config issues in development
if (process.env.NODE_ENV === 'development') {
  Object.entries(firebaseConfig).forEach(([key, value]) => {
    if (value && value.includes('\n')) {
      console.warn(`Firebase config ${key} contains newline characters`);
    }
  });
}

// Initialize Firebase only if it hasn't been initialized
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// Initialize Firebase services
export const auth = getAuth(app);

// Initialize Firestore with enhanced settings
export const db = initializeFirestore(app, {
  cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  experimentalAutoDetectLongPolling: true // Auto-detect when to use long polling (removes conflict)
});

export const storage = getStorage(app);

// Enable offline persistence with proper error handling
if (typeof window !== 'undefined') {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      // Multiple tabs open, persistence can only be enabled in one tab at a time
      console.warn('Firebase persistence failed: Multiple tabs open');
    } else if (err.code === 'unimplemented') {
      // The current browser doesn't support persistence
      console.warn('Firebase persistence not supported in this browser');
    }
  });
  
  // Monitor network state
  window.addEventListener('online', () => {
    console.log('Network connection restored');
    enableNetwork(db).catch(console.error);
  });
  
  window.addEventListener('offline', () => {
    console.log('Network connection lost');
    disableNetwork(db).catch(console.error);
  });
}

// Initialize Analytics conditionally (only in browser and if supported)
export const initAnalytics = async () => {
  if (typeof window !== 'undefined' && await isSupported()) {
    return getAnalytics(app);
  }
  return null;
};

// Helper to check if Firebase is configured
export const isFirebaseConfigured = () => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  );
};

// Helper to check network status
export const isOnline = () => {
  return typeof window !== 'undefined' ? navigator.onLine : true;
};

// Helper to retry Firestore operations
export const retryFirestoreOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  delay = 1000
): Promise<T> => {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      // Check if it's an offline error
      if (error?.code === 'unavailable' || error?.message?.includes('offline')) {
        console.warn(`Firestore operation failed (attempt ${i + 1}/${maxRetries}):`, error.message);
        
        // Wait before retrying
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
        }
      } else {
        // If it's not an offline error, throw immediately
        throw error;
      }
    }
  }
  
  throw lastError;
};

export default app;